import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../layouts/PageTitleContext';
import useBreadcrumbSetup from '../components/useBreadcrumbSetup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPaperPlane, faTrash, faEnvelope, faComment, faMobileAlt,
    faCheckCircle, faClock, faFileAlt, faBullhorn, faUsers,
    faEye, faMousePointer, faChartLine,
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import InlineLoader from '../components/Loader/InlineLoader';
import { useAuth } from '../context/AuthProvider';
import { API_BASE_URL } from '../api';
import './CampaignDetail.scss';

const getCanalMeta = (t) => ({
    email:    { label: t('Email'),    icon: faEnvelope,  cls: 'cd-badge--email' },
    whatsapp: { label: t('WhatsApp'), icon: faComment,   cls: 'cd-badge--whatsapp' },
    sms:      { label: t('SMS'),      icon: faMobileAlt, cls: 'cd-badge--sms' },
});

const getStatutMeta = (t) => ({
    brouillon: { label: t('campaigns.status.draft'),     cls: 'cd-badge--brouillon', icon: faFileAlt },
    planifie:  { label: t('campaigns.status.scheduled'), cls: 'cd-badge--planifie',  icon: faClock },
    en_cours:  { label: t('campaigns.status.sending'),   cls: 'cd-badge--en_cours',  icon: faPaperPlane },
    envoye:    { label: t('campaigns.status.sent'),      cls: 'cd-badge--envoye',    icon: faCheckCircle },
    erreur:    { label: t('campaigns.status.error'),     cls: 'cd-badge--erreur',    icon: faBullhorn },
});

function MetricCard({ icon, label, value, sub, color }) {
    return (
        <div className="cd-metric">
            <div className="cd-metric-icon" style={{ background: color + '18', color }}>
                <FontAwesomeIcon icon={icon} />
            </div>
            <div>
                <div className="cd-metric-val">{value}</div>
                <div className="cd-metric-label">{label}</div>
                {sub && <div className="cd-metric-sub">{sub}</div>}
            </div>
        </div>
    );
}

export default function CampaignDetail() {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const { setTitle } = usePageTitle();
    const { hasRight } = useAuth();
    const canEdit = hasRight('MARKETING_EDIT');
    const canDelete = hasRight('MARKETING_DELETE');

    const CANAL_META = getCanalMeta(t);
    const STATUT_META = getStatutMeta(t);

    const [campaign, setCampaign] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);

    useEffect(() => {
        axios.get(`${API_BASE_URL}/api/v1/marketing/campaigns/${id}`, { withCredentials: true })
            .then(({ data }) => {
                setCampaign(data);
                setTitle(data.nom);
                document.title = data.nom;
            })
            .catch(() => { toast.error(t('campaign_detail.toast.not_found')); navigate('/marketing/campaigns'); })
            .finally(() => setLoading(false));
    }, [id, t]);

    useBreadcrumbSetup([
        { label: t('Marketing'), url: '/marketing/campaigns' },
        { label: t('Campaigns'), url: '/marketing/campaigns' },
        { label: campaign?.nom || '...' },
    ]);

    const handleSend = async () => {
        if (!window.confirm(t('campaigns.confirm.send'))) return;
        setSending(true);
        try {
            const { data } = await axios.post(
                `${API_BASE_URL}/api/v1/marketing/campaigns/${id}/send`,
                {},
                { withCredentials: true }
            );
            toast.success(t('campaigns.toast.sent'));
            setCampaign(data);
        } catch (err) {
            toast.error(err?.response?.data?.message || t('campaigns.toast.send_error'));
        } finally {
            setSending(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm(t('campaigns.confirm.delete'))) return;
        try {
            await axios.delete(`${API_BASE_URL}/api/v1/marketing/campaigns/${id}`, { withCredentials: true });
            toast.success(t('campaigns.toast.deleted'));
            navigate('/marketing/campaigns');
        } catch (err) {
            toast.error(err?.response?.data?.message || t('campaigns.toast.delete_error'));
        }
    };

    if (loading) return <InlineLoader loadingMessage={t('campaign_detail.loading')} />;

    const c = campaign;
    const canal = CANAL_META[c.canal] || CANAL_META.email;
    const statut = STATUT_META[c.statut] || STATUT_META.brouillon;
    const sent = c.nbEnvoyes || 0;
    const opened = c.nbOuverts || 0;
    const clicks = c.nbClics || 0;
    const dest = c.destinataires || 0;
    const openRate  = sent > 0 ? ((opened / sent) * 100).toFixed(1) : '0';
    const clickRate = opened > 0 ? ((clicks / opened) * 100).toFixed(1) : '0';
    const delivRate = dest > 0 ? ((sent / dest) * 100).toFixed(1) : '0';

    const isSent = c.statut === 'envoye';

    return (
        <div className="cd-page">

            {/* Header */}
            <div className="cd-header">
                <div className="cd-header-left">
                    <h1 className="cd-title">{c.nom}</h1>
                    <div className="cd-badges">
                        <span className={`cd-badge ${canal.cls}`}>
                            <FontAwesomeIcon icon={canal.icon} /> {canal.label}
                        </span>
                        <span className={`cd-badge ${statut.cls}`}>
                            <FontAwesomeIcon icon={statut.icon} /> {statut.label}
                        </span>
                    </div>
                </div>
                <div className="cd-header-actions">
                    {!isSent && canEdit && (
                        <button className="cd-btn cd-btn--send" onClick={handleSend} disabled={sending}>
                            <FontAwesomeIcon icon={faPaperPlane} />
                            {sending ? t('campaign_detail.action.sending') : t('campaign_detail.action.send')}
                        </button>
                    )}
                    {!isSent && canDelete && (
                        <button className="cd-btn cd-btn--danger" onClick={handleDelete}>
                            <FontAwesomeIcon icon={faTrash} /> {t('Delete')}
                        </button>
                    )}
                </div>
            </div>

            {/* Metrics (only when sent) */}
            {isSent && (
                <div className="cd-metrics">
                    <MetricCard icon={faUsers}       label={t('campaigns.column.recipients')} value={dest.toLocaleString()}   color="#2563eb" />
                    <MetricCard icon={faPaperPlane}  label={t('campaigns.column.sent')}       value={sent.toLocaleString()}   color="#10b981"
                        sub={t('campaign_detail.metric.delivery_sub', { rate: delivRate })} />
                    <MetricCard icon={faEye}         label={t('campaigns.column.opens')}      value={opened.toLocaleString()} color="#8b5cf6"
                        sub={t('campaign_detail.metric.open_sub', { rate: openRate })} />
                    <MetricCard icon={faMousePointer} label={t('campaign_detail.metric.clicks')} value={clicks.toLocaleString()} color="#f59e0b"
                        sub={t('campaign_detail.metric.click_sub', { rate: clickRate })} />
                </div>
            )}

            {/* Stats bar */}
            {isSent && dest > 0 && (
                <div className="cd-bars">
                    <div className="cd-bar-row">
                        <span>{t('campaign_detail.bar.delivery_rate')}</span>
                        <div className="cd-bar-track">
                            <div className="cd-bar-fill" style={{ width: `${delivRate}%`, background: '#10b981' }} />
                        </div>
                        <span className="cd-bar-pct">{delivRate}%</span>
                    </div>
                    <div className="cd-bar-row">
                        <span>{t('campaigns.stat.open_rate')}</span>
                        <div className="cd-bar-track">
                            <div className="cd-bar-fill" style={{ width: `${openRate}%`, background: '#8b5cf6' }} />
                        </div>
                        <span className="cd-bar-pct">{openRate}%</span>
                    </div>
                    <div className="cd-bar-row">
                        <span>{t('campaign_detail.bar.click_rate')}</span>
                        <div className="cd-bar-track">
                            <div className="cd-bar-fill" style={{ width: `${clickRate}%`, background: '#f59e0b' }} />
                        </div>
                        <span className="cd-bar-pct">{clickRate}%</span>
                    </div>
                </div>
            )}

            {/* Info grid */}
            <div className="cd-info-grid">
                <div className="cd-info-card">
                    <h3>{t('campaign_detail.info.details_title')}</h3>
                    <dl>
                        {c.sujet && (
                            <>
                                <dt>{t('campaign_create.summary.subject')}</dt>
                                <dd>{c.sujet}</dd>
                            </>
                        )}
                        <dt>{t('Template')}</dt>
                        <dd>
                            {c.template ? (
                                <button
                                    className="cd-link"
                                    onClick={() => navigate(`/marketing/templates/${c.template.id}/edit`)}
                                >
                                    {c.template.nom}
                                </button>
                            ) : t('campaign_create.no_template_label')}
                        </dd>
                        <dt>{t('campaign_detail.info.created_at')}</dt>
                        <dd>{c.createdAt ? new Date(c.createdAt).toLocaleDateString('fr-FR') : '—'}</dd>
                        {c.dateEnvoiPlanifie && (
                            <>
                                <dt>{t('campaign_create.summary.scheduled_send')}</dt>
                                <dd>{new Date(c.dateEnvoiPlanifie).toLocaleString('fr-FR')}</dd>
                            </>
                        )}
                        {c.dateEnvoiReel && (
                            <>
                                <dt>{t('campaign_detail.info.sent_at')}</dt>
                                <dd>{new Date(c.dateEnvoiReel).toLocaleString('fr-FR')}</dd>
                            </>
                        )}
                    </dl>
                </div>

                {c.contenuPersonnalise && (
                    <div className="cd-info-card">
                        <h3>{t('campaign_detail.info.custom_content_title')}</h3>
                        <pre className="cd-contenu">{c.contenuPersonnalise}</pre>
                    </div>
                )}
            </div>
        </div>
    );
}
